import React, { useState, useEffect, useRef, useCallback } from "react";
import { BsX, BsChevronDown } from "react-icons/bs";
import Modal from "react-modal";
import Swal from "sweetalert2";
import api from "../../config/configApi";

interface Image {
  imagem: string;
}

interface Material {
  codigoMaterial: string;
  quantidade: number;
  observacao: string;
  valor: number;
  item?: string;
  descricao?: string;
}

interface MaterialAPI {
  id: number;
  descricao: string;
  codigo: string;
  valor: number;
  status: number;
  criadoEm: string;
}

interface Patient {
  id: string;
  cd_paciente: string;
  nome: string;
  nuCpf: string;
  classe: string;
  ultimaVisita: string;
  visitaProgramada: string;
  statusVisitaPrograma: string;
  cep: string;
  bairro: string;
  localidade: string;
  regiao: string;
  fisio: string;
  fono: string;
  psico: string;
  nutri: string;
  curativo: boolean;
  alimentacaoEnteral: string;
  regimeCoparticipacao: string;
  lemedh: Material[];
  observacao: string;
  vacinacao: string;
  img?: string | Image[];
  dataInsercao: string;
  materiais?: Material[];
  status?: string;
  orgaoVacinacao?: string;
}

interface PatientModalProps {
  isOpen: boolean;
  onRequestClose: () => void;
  patient: Patient | null;
  onPatientSaved: () => void;
  materiais: any[];
}

const PatientModal: React.FC<PatientModalProps> = ({
  isOpen,
  onRequestClose,
  patient,
  onPatientSaved,
  materiais
}) => {
  const [newPatient, setNewPatient] = useState<Patient>({
    id: "",
    cd_paciente: "",
    nome: "",
    nuCpf: "",
    classe: "",
    ultimaVisita: "",
    visitaProgramada: "",
    statusVisitaPrograma: "PENDENTE",
    cep: "",
    bairro: "",
    localidade: "",
    regiao: "",
    fisio: "",
    fono: "",
    psico: "",
    nutri: "",
    curativo: false,
    alimentacaoEnteral: "",
    regimeCoparticipacao: "",
    lemedh: [],
    observacao: "",
    vacinacao: "",
    dataInsercao: "",
    materiais: [],
    status: "ATIVO",
    orgaoVacinacao: ""
  });

  const [, setBeneficiarios] = useState<any[]>([]);
  const [filteredBeneficiarios, setFilteredBeneficiarios] = useState<any[]>([]);
  const [showBeneficiariosDropdown, setShowBeneficiariosDropdown] = useState(false);
  const [materialOptions, setMaterialOptions] = useState<MaterialAPI[]>([]);
  const [isLoadingBeneficiarios, setIsLoadingBeneficiarios] = useState(false);
  const [isCpfFromBeneficiary, setIsCpfFromBeneficiary] = useState(false);
  const [isLoadingPatient, setIsLoadingPatient] = useState(false);
  const [isLoadingMaterials, setIsLoadingMaterials] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Função para carregar materiais da API
  const fetchMaterials = async () => {
    setIsLoadingMaterials(true);
    try {
      const token = localStorage.getItem("Frontend");
      if (!token) {
        console.error("Token não encontrado");
        return;
      }

      const response = await api.get("/sisclinic/sad/materiais", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data && Array.isArray(response.data)) {
        // Filtrar apenas materiais ativos (status 1) se necessário
        const materiaisAtivos = response.data.filter((material: MaterialAPI) => 
          material.status === 1 || material.status === undefined
        );
        setMaterialOptions(materiaisAtivos);
      }
    } catch (error) {
      console.error("Erro ao buscar materiais:", error);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Não foi possível carregar a lista de materiais.",
      });
    } finally {
      setIsLoadingMaterials(false);
    }
  };

  // Carregar materiais ao abrir o modal
  useEffect(() => {
    if (isOpen) {
      fetchMaterials();
    }
  }, [isOpen]);

  // Função para buscar classificação do paciente pelo CPF
  const fetchClassificacaoByCPF = async (cpf: string) => {
    if (!cpf || cpf.replace(/\D/g, '').length !== 11) return;

    try {
      const token = localStorage.getItem("Frontend");
      if (!token) {
        console.error("Token não encontrado");
        return;
      }

      // Buscar dados do paciente no SAD pelo CPF
      const response = await api.get(`/sisclinic/pacientes/cpf/${cpf.replace(/\D/g, '')}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data && response.data.avaliacoes && response.data.avaliacoes.length > 0) {
        // Encontrar a avaliação mais recente
        const avaliacoes = response.data.avaliacoes;
        const ultimaAvaliacao = avaliacoes.reduce((prev: any, current: any) => {
          return new Date(prev.dataAvaliacao) > new Date(current.dataAvaliacao) ? prev : current;
        });

        if (ultimaAvaliacao.classificacaoDescricao) {
          // Atualizar a classificação no estado
          setNewPatient(prevState => ({
            ...prevState,
            classe: ultimaAvaliacao.classificacaoDescricao
          }));
          
          console.log("Classificação encontrada:", ultimaAvaliacao.classificacaoDescricao);
        }
      } else {
        console.log("Nenhuma avaliação encontrada para este CPF");
      }
    } catch (error) {
      console.error("Erro ao buscar classificação:", error);
      // Não mostrar erro para o usuário, pois pode ser um paciente novo
    }
  };

const loadPatientData = useCallback(async (patientId: string, cpf: string) => {
  setIsLoadingPatient(true);
  try {
    const token = localStorage.getItem("Frontend");
    if (!token) {
      Swal.fire({
        icon: "error",
        title: "Erro de autenticação",
        text: "Token de autenticação não encontrado. Faça login novamente.",
      });
      return;
    }
    // Buscar dados completos do paciente pelo CPF
    const response = await api.get(`/sisclinic/pacientes/cpf/${cpf.replace(/\D/g, '')}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.data) {
      const pacienteData = response.data;
      
      // Buscar a classificação mais recente das avaliações
      let classificacaoAtual = "";
      if (pacienteData.avaliacoes && pacienteData.avaliacoes.length > 0) {
        // Ordenar avaliações por data (mais recente primeiro)
        const avaliacoesOrdenadas = [...pacienteData.avaliacoes].sort((a, b) => 
          new Date(b.dataAvaliacao).getTime() - new Date(a.dataAvaliacao).getTime()
        );
        classificacaoAtual = avaliacoesOrdenadas[0].classificacaoDescricao || "";
      }
      
      // Processar materiais do paciente
      const materiaisList = pacienteData.materiais || [];
      const lemedhList: Material[] = materiaisList.map((material: any) => {
        // Encontrar o material correspondente na lista da API
        const materialInfo = materialOptions.find(m => m.codigo === material.codigoMaterial);
        
        return {
          codigoMaterial: material.codigoMaterial || "",
          quantidade: material.quantidade || 1,
          observacao: material.observacao || material.descricao || "",
          valor: material.valor || materialInfo?.valor || 0,
          descricao: material.descricao || materialInfo?.descricao || material.observacao || ""
        };
      }).filter((item: Material) => item.codigoMaterial !== "");

      // Usar os dados de endereço diretamente da API
      setNewPatient({
        id: patientId,
        cd_paciente: pacienteData.cdPaciente || pacienteData.cd_paciente || "",
        nome: pacienteData.nmPaciente || pacienteData.nomePaciente || "",
        nuCpf: formatCPF(pacienteData.nuCpf || ""),
        classe: classificacaoAtual || pacienteData.classificacao || "",
        ultimaVisita: pacienteData.ultimaVisita || "",
        visitaProgramada: pacienteData.proximaVisita || "",
        statusVisitaPrograma: pacienteData.statusVisitaProgamada ? "CONCLUÍDO" : "PENDENTE",
        cep: pacienteData.cdCepEndereco || pacienteData.cep || "",
        bairro: pacienteData.nmBairroEndereco || pacienteData.bairro || "",
        localidade: pacienteData.nmCidadeEndereco || pacienteData.cidade || "",
        regiao: pacienteData.regiao || pacienteData.nmUfEndereco || "",
        fisio: pacienteData.fisio || "",
        fono: pacienteData.fono || "",
        psico: pacienteData.psico || "",
        nutri: pacienteData.nutri || "",
        curativo: pacienteData.curativo || false,
        alimentacaoEnteral: pacienteData.alimentacaoEnteral || "",
        regimeCoparticipacao: pacienteData.regimeCoparticipacao || "",
        lemedh: lemedhList,
        observacao: pacienteData.observacao || "",
        vacinacao: pacienteData.orgaoVacinacao || "",
        dataInsercao: pacienteData.dtCadPaciente || pacienteData.dataInsercao || "",
        materiais: lemedhList,
        status: pacienteData.status || "ATIVO",
        orgaoVacinacao: pacienteData.orgaoVacinacao || ""
      });
      setIsCpfFromBeneficiary(true);
    }
  } catch (error) {
    console.error("Erro ao carregar dados do paciente:", error);
    Swal.fire({
      icon: "error",
      title: "Erro",
      text: "Não foi possível carregar os dados completos do paciente.",
    });
  } finally {
    setIsLoadingPatient(false);
  }
}, [materialOptions]); // Add materialOptions as dependency

  useEffect(() => {
    if (patient && patient.id) {
      // Se for edição, carregar os dados completos do paciente
      const cpf = patient.nuCpf || "";
      loadPatientData(patient.id, cpf);
    } else {
      setNewPatient({
        id: "",
        cd_paciente: "",
        nome: "",
        nuCpf: "",
        classe: "",
        ultimaVisita: "",
        visitaProgramada: "",
        statusVisitaPrograma: "PENDENTE",
        cep: "",
        bairro: "",
        localidade: "",
        regiao: "",
        fisio: "",
        fono: "",
        psico: "",
        nutri: "",
        curativo: false,
        alimentacaoEnteral: "",
        regimeCoparticipacao: "",
        lemedh: [],
        observacao: "",
        vacinacao: "",
        dataInsercao: "",
        materiais: [],
        status: "ATIVO",
        orgaoVacinacao: ""
      });
      setIsCpfFromBeneficiary(false);
    }
  }, [patient, loadPatientData]);

  // Atualizar lemedh quando materialOptions mudar
  useEffect(() => {
    if (patient && patient.id && materialOptions.length > 0) {
      const cpf = patient.nuCpf || "";
      loadPatientData(patient.id, cpf);
    }
  }, [materialOptions, patient, loadPatientData]);

  // Função para formatar CPF
  const formatCPF = (cpf: string) => {
    if (!cpf) return "";
    cpf = cpf.replace(/\D/g, '');
    
    if (cpf.length <= 11) {
      cpf = cpf.replace(/(\d{3})(\d)/, '$1.$2');
      cpf = cpf.replace(/(\d{3})(\d)/, '$1.$2');
      cpf = cpf.replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    }
    return cpf;
  };

  // Função para buscar beneficiários com base no texto digitado
  const fetchBeneficiarios = async (searchTerm: string) => {
    if (!searchTerm.trim()) {
      setFilteredBeneficiarios([]);
      setShowBeneficiariosDropdown(false);
      return;
    }

    setIsLoadingBeneficiarios(true);
    try {
      const token = localStorage.getItem("Frontend");

      if (!token) {
        Swal.fire({
          icon: "error",
          title: "Erro de autenticação",
          text: "Token de autenticação não encontrado. Faça login novamente.",
        });
        setIsLoadingBeneficiarios(false);
        return;
      }

      // Buscar beneficiários pelo nome
      const response = await api.get("/sisclinic/pacientes", {
        params: {
          nome: searchTerm.trim()
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data && Array.isArray(response.data)) {
        setBeneficiarios(response.data);
        
        // Filtrar localmente para melhor performance
        const filtered = response.data.filter(beneficiario =>
          beneficiario &&
          beneficiario.nmPaciente &&
          beneficiario.nmPaciente.toLowerCase().includes(searchTerm.toLowerCase())
        );
        
        setFilteredBeneficiarios(filtered);
        
        // Mostrar dropdown se houver resultados
        if (filtered.length > 0) {
          setShowBeneficiariosDropdown(true);
        } else {
          setShowBeneficiariosDropdown(false);
        }
      }
    } catch (error) {
      console.error("Erro ao buscar beneficiários:", error);
      setFilteredBeneficiarios([]);
      setShowBeneficiariosDropdown(false);
    } finally {
      setIsLoadingBeneficiarios(false);
    }
  };

  // Debounce para evitar muitas chamadas à API
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (!patient && newPatient.nome && newPatient.nome.trim().length >= 3) {
        fetchBeneficiarios(newPatient.nome);
      } else if (!patient && (!newPatient.nome || newPatient.nome.trim().length < 3)) {
        setFilteredBeneficiarios([]);
        setShowBeneficiariosDropdown(false);
      }
    }, 500); // 500ms de delay

    return () => clearTimeout(debounceTimer);
  }, [newPatient.nome, patient]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowBeneficiariosDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Monitorar mudanças no CPF para buscar classificação
  useEffect(() => {
    if (newPatient.nuCpf && newPatient.nuCpf.replace(/\D/g, '').length === 11) {
      // Buscar classificação quando o CPF for válido
      fetchClassificacaoByCPF(newPatient.nuCpf);
    }
  }, [newPatient.nuCpf]);

  const handleLeMedhSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedCode = e.target.value;
    if (!selectedCode) return;

    // Encontrar o material selecionado
    const selectedMaterial = materialOptions.find(material => material.codigo === selectedCode);
    if (!selectedMaterial) return;

    // Verificar se o material já foi adicionado
    const alreadyAdded = newPatient.lemedh.some(item => item.codigoMaterial === selectedCode);
    if (alreadyAdded) {
      Swal.fire({
        icon: "warning",
        title: "Material já adicionado",
        text: "Este material já está na lista.",
      });
      return;
    }

    // Adicionar o material à lista
    const newMaterial: Material = {
      codigoMaterial: selectedMaterial.codigo,
      quantidade: 1,
      observacao: selectedMaterial.descricao,
      valor: selectedMaterial.valor,
      descricao: selectedMaterial.descricao
    };

    setNewPatient(prevState => ({
      ...prevState,
      lemedh: [...prevState.lemedh, newMaterial]
    }));

    // Resetar o select
    e.target.value = "";
  };

  const handleRemoveLeMedh = (index: number) => {
    setNewPatient((prevState) => ({
      ...prevState,
      lemedh: prevState.lemedh.filter((_, i) => i !== index),
    }));
  };

  // Função para atualizar quantidade de um material
  const handleUpdateMaterialQuantity = (index: number, quantidade: number) => {
    if (quantidade < 1) return;
    
    setNewPatient(prevState => {
      const updatedLemedh = [...prevState.lemedh];
      updatedLemedh[index] = {
        ...updatedLemedh[index],
        quantidade: quantidade
      };
      
      return {
        ...prevState,
        lemedh: updatedLemedh
      };
    });
  };

  // Atualizar a função handleBeneficiarySelect
  const handleBeneficiarySelect = async (beneficiario: any) => {
    if (beneficiario) {
      // Usar os dados completos do beneficiário da API
      const newCep = beneficiario.cdCepEndereco || "";
      const newCpf = beneficiario.nuCpf || "";
      const newCdBeneficiario = beneficiario.cdPaciente || "";

      // Formatar o CPF antes de salvar
      const cpfFormatado = formatCPF(newCpf);

      setNewPatient((prevState) => ({
        ...prevState,
        nome: beneficiario.nmPaciente || "",
        nuCpf: cpfFormatado,
        cd_paciente: newCdBeneficiario,
        cep: newCep,
        bairro: beneficiario.nmBairroEndereco || "", // Preencher bairro
        localidade: beneficiario.nmCidadeEndereco || "", // Preencher cidade
        regiao: beneficiario.nmUfEndereco || "", // Preencher estado
        classe: "", // Limpar a classificação inicialmente
      }));

      // Marcar que o CPF veio de um beneficiário selecionado
      setIsCpfFromBeneficiary(true);

      // Buscar classificação automaticamente usando o CPF
      if (cpfFormatado.replace(/\D/g, '').length === 11) {
        await fetchClassificacaoByCPF(cpfFormatado);
      }

      // Agora não precisamos mais consultar o ViaCEP
      // Já temos todos os dados de endereço da API
    } else {
      setNewPatient((prevState) => ({
        ...prevState,
        nome: "",
        nuCpf: "",
        classe: "", // Limpar a classificação
        cep: "",
        bairro: "",
        localidade: "",
        regiao: "",
      }));
      setIsCpfFromBeneficiary(false);
    }
    setShowBeneficiariosDropdown(false);
  };

  const handleInputChange = async (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;

    if (name === "nome" && !patient) {
      // Atualizar o nome
      setNewPatient((prevState) => ({
        ...prevState,
        nome: value,
      }));

      // Se o campo de nome for limpado, limpar também o CPF e resetar flag
      if (!value.trim()) {
        setNewPatient((prevState) => ({
          ...prevState,
          nuCpf: "",
          classe: "", // Limpar a classificação
          cep: "",
          bairro: "",
          localidade: "",
          regiao: "",
        }));
        setIsCpfFromBeneficiary(false);
      }
    }
    else if (name === "nuCpf") {
      // Formatar CPF enquanto o usuário digita
      const cpfFormatado = formatCPF(value);
      setNewPatient((prevState) => ({
        ...prevState,
        nuCpf: cpfFormatado,
      }));

      // Buscar classificação automaticamente quando o CPF for completo
      if (cpfFormatado.replace(/\D/g, '').length === 11) {
        await fetchClassificacaoByCPF(cpfFormatado);
      }
    }
    else if (name === "cep") {
      setNewPatient((prevState) => ({
        ...prevState,
        cep: value,
      }));

      // Se o usuário digitar manualmente, consultar ViaCEP apenas se necessário
      if (value.length === 8 && !isCpfFromBeneficiary) {
        try {
          const response = await fetch(
            `https://viacep.com.br/ws/${value}/json/`
          );
          const data = await response.json();

          if (data && !data.erro) {
            setNewPatient((prevState) => ({
              ...prevState,
              bairro: data.bairro || "",
              localidade: data.localidade || "",
              regiao: data.regiao || "",
            }));
          } else {
            alert("CEP inválido ou não encontrado!");
          }
        } catch (error) {
          console.error("Erro ao buscar o bairro:", error);
        }
      }
    } else if (type === "checkbox") {
      setNewPatient((prevState) => ({
        ...prevState,
        [name]: (e.target as HTMLInputElement).checked,
      }));
    } else if (type === "radio") {
      if (name === "curativo") {
        setNewPatient((prevState) => ({
          ...prevState,
          curativo: value === "SIM",
        }));
      } else if (name === "regimeCoparticipacao") {
        setNewPatient((prevState) => ({
          ...prevState,
          regimeCoparticipacao: value,
        }));
      } else {
        setNewPatient((prevState) => ({
          ...prevState,
          [name]: value,
        }));
      }
    } else if (name === "ultimaVisita") {
      const formattedValue = formatDateToDisplay(value);
      const proximaVisita = calculateProximaVisita(
        formattedValue,
        newPatient.classe
      );

      setNewPatient((prevState) => ({
        ...prevState,
        ultimaVisita: formattedValue,
        visitaProgramada: proximaVisita,
      }));
    } else if (name === "classe") {
      const proximaVisita = calculateProximaVisita(
        newPatient.ultimaVisita,
        value
      );

      setNewPatient((prevState) => ({
        ...prevState,
        classe: value,
        visitaProgramada: proximaVisita,
      }));
    } else {
      setNewPatient((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    }
  };

  const formatDateToDisplay = (date: string) => {
    if (!date) return "";
    const [year, month, day] = date.split("-");
    return `${year}-${month}-${day}`;
  };

  const calculateProximaVisita = (
    ultimaVisita: string,
    classificacao: string
  ) => {
    if (!ultimaVisita) return "";
    const ultimaVisitaDate = new Date(ultimaVisita);

    if (isNaN(ultimaVisitaDate.getTime())) {
      return "";
    }

    let proximaVisitaDate;

    switch (classificacao) {
      case "ALTA":
        proximaVisitaDate = new Date(
          ultimaVisitaDate.setMonth(ultimaVisitaDate.getMonth() + 1)
        );
        break;
      case "MÉDIA":
        proximaVisitaDate = new Date(
          ultimaVisitaDate.setMonth(ultimaVisitaDate.getMonth() + 2)
        );
        break;
      case "BAIXA":
        proximaVisitaDate = new Date(
          ultimaVisitaDate.setMonth(ultimaVisitaDate.getMonth() + 4)
        );
        break;
      case "REDE":
        proximaVisitaDate = new Date(
          ultimaVisitaDate.setMonth(ultimaVisitaDate.getMonth() + 6)
        );
        break;
      case "SEM INDICACAO DOMICILIAR":
        proximaVisitaDate = new Date(
          ultimaVisitaDate.setMonth(ultimaVisitaDate.getMonth())
        );
        break;
      default:
        return "";
    }

    return proximaVisitaDate.toISOString().split("T")[0];
  };

  const formatDateToInput = (dateString: string | undefined) => {
    if (!dateString) return "";
    return new Date(dateString).toISOString().split("T")[0];
  };

  // Calcular valor total dos materiais
  const calcularValorTotal = () => {
    return newPatient.lemedh.reduce((total, material) => {
      return total + (material.valor * material.quantidade);
    }, 0);
  };

  // Função para formatar valor em Reais
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const requiredFields = [
      "nome",
      "localidade",
      "regiao",
      "fisio",
      "fono",
      "psico",
      "nutri",
      "alimentacaoEnteral",
      "regimeCoparticipacao",
    ];

    const missingFields = requiredFields.filter(
      (field) => !newPatient[field as keyof typeof newPatient]
    );

    if (missingFields.length > 0) {
      Swal.fire({
        icon: "warning",
        title: "Campos obrigatórios faltando!",
        text: `Por favor, preencha os seguintes campos: ${missingFields.join(
          ", "
        )}`,
      });
      return;
    }

    const token = localStorage.getItem("Frontend");

    if (!token) {
      Swal.fire({
        icon: "error",
        title: "Erro de autenticação",
        text: "Token de autenticação não encontrado. Faça login novamente.",
      });
      return;
    }

    // Remover formatação do CPF antes de enviar para a API
    const cpfSemFormatacao = newPatient.nuCpf.replace(/\D/g, '');

    // Preparar os materiais para a API
    const materiaisParaAPI = newPatient.lemedh.map(item => {
      return {
        codigoMaterial: item.codigoMaterial,
        quantidade: item.quantidade,
        observacao: item.observacao || item.descricao || "",
        valor: item.valor
      };
    });

    const dadosParaAPI = {
      nuCpf: cpfSemFormatacao, // Mantém o CPF no corpo também
      nomePaciente: newPatient.nome,
      ultimaVisita: newPatient.ultimaVisita || "",
      proximaVisita: newPatient.visitaProgramada || "",
      statusVisitaProgamada: newPatient.statusVisitaPrograma === "CONCLUÍDO",
      cep: newPatient.cep,
      bairro: newPatient.bairro,
      cidade: newPatient.localidade,
      regiao: newPatient.regiao,
      fisio: newPatient.fisio,
      fono: newPatient.fono,
      psico: newPatient.psico,
      nutri: newPatient.nutri,
      curativo: newPatient.curativo,
      alimentacaoEnteral: newPatient.alimentacaoEnteral,
      regimeCoparticipacao: newPatient.regimeCoparticipacao,
      observacao: newPatient.observacao,
      orgaoVacinacao: newPatient.vacinacao || "SAD",
      status: newPatient.status || "ATIVO",
      materiais: materiaisParaAPI
    };

    try {
      console.log("Dados para API:", dadosParaAPI);
      console.log("Materiais formatados:", materiaisParaAPI);
      console.log("Valor total dos materiais:", calcularValorTotal());
      
      if (newPatient.id) {
        // ATUALIZAR - usar o CPF como chave
        const response = await api.put(
          `/sisclinic/sad/pacientes/cpf/${cpfSemFormatacao}`,
          dadosParaAPI,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json"
            },
          }
        );
        console.log("Resposta da atualização:", response.data);
        
        Swal.fire({
          icon: "success",
          title: "Atualização bem-sucedida!",
          text: "Paciente atualizado com sucesso.",
        }).then(() => {
          onPatientSaved();
        });
      } else {
        // CRIAR NOVO
        const response = await api.post("/sisclinic/sad/pacientes", dadosParaAPI, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
        });
        console.log("Resposta do cadastro:", response.data);
        
        Swal.fire({
          icon: "success",
          title: "Cadastro bem-sucedido!",
          text: "Paciente cadastrado com sucesso.",
        }).then(() => {
          onPatientSaved();
        });
      }
   } catch (error) {
      console.error("Erro ao processar paciente:", error);
      
      // Log mais detalhado do erro
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as any;
        console.error("Dados da resposta do erro:", axiosError.response?.data);
        console.error("Status do erro:", axiosError.response?.status);
        console.error("Headers do erro:", axiosError.response?.headers);
        
        Swal.fire({
          icon: "error",
          title: "Erro na API!",
          text: `Status: ${axiosError.response?.status} - ${JSON.stringify(axiosError.response?.data)}`,
        });
      } else if (error && typeof error === 'object' && 'request' in error) {
        const axiosError = error as any;
        console.error("Request do erro:", axiosError.request);
        Swal.fire({
          icon: "error",
          title: "Erro de conexão!",
          text: "Não foi possível conectar ao servidor. Verifique sua conexão.",
        });
      } else {
        const genericError = error as Error;
        Swal.fire({
          icon: "error",
          title: "Erro!",
          text: `Ocorreu um erro ao processar o paciente: ${genericError.message || 'Erro desconhecido'}`,
        });
      }
    }
  };

  const getClassBorderColor = (classType: string) => {
    switch (classType) {
      case "BAIXA":
        return "border-l-green-500 bg-green-50";
      case "REDE":
        return "border-l-blue-500 bg-blue-50";
      case "MÉDIA":
        return "border-l-yellow-500 bg-yellow-50";
      case "ALTA":
        return "border-l-orange-500 bg-orange-50";
      default:
        return "border-l-gray-500 bg-gray-50";
    }
  };

  if (isLoadingPatient && patient) {
    return (
      <Modal
        isOpen={isOpen}
        onRequestClose={onRequestClose}
        contentLabel="Carregando paciente"
        className="bg-white p-6 rounded-xl shadow-xl max-w-4xl mx-auto my-8 max-h-[90vh] overflow-y-auto"
        overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      >
        <div className="flex flex-col items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
          <p className="text-gray-600">Carregando dados do paciente...</p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      contentLabel={newPatient.id ? "Editar Paciente" : "Adicionar Novo Paciente"}
      className="bg-white p-6 rounded-xl shadow-xl max-w-4xl mx-auto my-8 max-h-[90vh] overflow-y-auto"
      overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
    >
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">
          {newPatient.id ? "Editar Paciente" : "Adicionar Novo Paciente"}
        </h2>
        <button
          onClick={onRequestClose}
          className="text-gray-500 hover:text-gray-700"
        >
          <BsX size={24} />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2 relative" ref={dropdownRef}>
            <label
              htmlFor="nome"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Nome
            </label>
            {newPatient.id ? (
              <input
                id="nome"
                type="text"
                name="nome"
                value={newPatient.nome}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled
              />
            ) : (
              <div className="relative">
                <div className="flex items-center">
                  <input
                    id="nome"
                    type="text"
                    name="nome"
                    value={newPatient.nome}
                    onChange={handleInputChange}
                    onFocus={() => {
                      if (newPatient.nome.trim().length >= 3) {
                        setShowBeneficiariosDropdown(true);
                      }
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10"
                    placeholder="Digite o nome do paciente (mínimo 3 caracteres)"
                    autoComplete="off"
                  />
                  <BsChevronDown 
                    className="absolute right-3 text-gray-400 cursor-pointer" 
                    onClick={() => {
                      if (newPatient.nome.trim().length >= 3) {
                        setShowBeneficiariosDropdown(!showBeneficiariosDropdown);
                      }
                    }}
                  />
                </div>
                
                {showBeneficiariosDropdown && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {isLoadingBeneficiarios ? (
                      <div className="px-4 py-3 text-center text-gray-500">
                        <div className="inline-block animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-blue-500 mr-2"></div>
                        Buscando beneficiários...
                      </div>
                    ) : filteredBeneficiarios.length > 0 ? (
                      filteredBeneficiarios.map((beneficiario) => (
                        <div
                          key={beneficiario.cdPaciente || beneficiario.nmPaciente}
                          className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                          onClick={() => handleBeneficiarySelect(beneficiario)}
                        >
                          <div className="font-medium text-gray-900">
                            {beneficiario.nmPaciente}
                          </div>
                          {beneficiario.nuCpf && (
                            <div className="text-sm text-black-400">
                              CPF: {formatCPF(beneficiario.nuCpf)}
                            </div>
                          )}                        
                        </div>
                      ))
                    ) : (
                      <div className="px-4 py-3 text-gray-500">
                        {newPatient.nome.trim().length >= 3 
                          ? "Nenhum beneficiário encontrado" 
                          : "Digite pelo menos 3 caracteres para buscar"}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div>
            <label
              htmlFor="nuCpf"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              CPF
            </label>
            <input
              id="nuCpf"
              type="text"
              name="nuCpf"
              value={newPatient.nuCpf}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="000.000.000-00"
              maxLength={14}
              disabled={!!newPatient.id || isCpfFromBeneficiary}
              title={isCpfFromBeneficiary ? "CPF preenchido automaticamente do beneficiário selecionado" : ""}
            />
            {isCpfFromBeneficiary && !newPatient.id && (
              <p className="text-xs text-blue-600 mt-1">
                CPF preenchido automaticamente do beneficiário selecionado
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="classe"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Classificação
            </label>
            <select
              id="classe"
              name="classe"
              value={newPatient.classe}
              onChange={handleInputChange}
              className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${getClassBorderColor(
                newPatient.classe
              )}`}
            >
              <option value="">Selecione uma classificação</option>
              <option value="ALTA">Alta</option>
              <option value="BAIXA">Baixa</option>
              <option value="REDE">Rede</option>
              <option value="MÉDIA">Média</option>
              <option value="SEM INDICACAO DOMICILIAR">
                Sem Indicação Domiciliar
              </option>
            </select>
            {newPatient.nuCpf && newPatient.nuCpf.replace(/\D/g, '').length === 11 && (
              <p className="text-xs text-green-600 mt-1">
                {newPatient.classe 
                  ? `Classificação encontrada: ${newPatient.classe}`
                  : "Buscando classificação..."}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="ultimaVisita"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Última Visita
            </label>
            <input
              id="ultimaVisita"
              type="date"
              name="ultimaVisita"
              value={formatDateToInput(newPatient.ultimaVisita)}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label
              htmlFor="visitaProgramada"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Próxima Visita
            </label>
            <input
              id="visitaProgramada"
              type="date"
              name="visitaProgramada"
              value={formatDateToInput(newPatient.visitaProgramada)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-100"
              disabled
            />
          </div>

          <div>
            <label
              htmlFor="statusVisitaPrograma"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Status da Visita Programada
            </label>
            <select
              id="statusVisitaPrograma"
              name="statusVisitaPrograma"
              value={newPatient.statusVisitaPrograma}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="PENDENTE">Pendente</option>
              <option value="CONCLUÍDO">Concluído</option>
              <option value="CANCELADO">Cancelado</option>
            </select>
          </div>

          <div>
            <label
              htmlFor="cep"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              CEP
            </label>
            <input
              id="cep"
              type="text"
              name="cep"
              value={newPatient.cep}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="00000-000"
              maxLength={8}
            />
          </div>

          <div>
            <label
              htmlFor="bairro"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Bairro
            </label>
            <input
              id="bairro"
              type="text"
              name="bairro"
              value={newPatient.bairro}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-100"
              disabled
            />
          </div>

          <div>
            <label
              htmlFor="localidade"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Cidade
            </label>
            <input
              id="localidade"
              type="text"
              name="localidade"
              value={newPatient.localidade}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label
              htmlFor="regiao"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Região
            </label>
            <input
              id="regiao"
              type="text"
              name="regiao"
              value={newPatient.regiao}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label
              htmlFor="fisio"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Fisioterapeuta
            </label>
            <select
              id="fisio"
              name="fisio"
              value={newPatient.fisio}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Selecione um(a) Fisioterapeuta</option>
              <option value="-">-</option>
              <option value="DIEGO">Diego</option>
              <option value="RAYSSA">Rayssa</option>
              <option value="TERESA">Teresa</option>
            </select>
          </div>

          <div>
            <label
              htmlFor="fono"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Fonoaudiólogo
            </label>
            <select
              id="fono"
              name="fono"
              value={newPatient.fono}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Selecione um(a) Fonoaudiólogo</option>
              <option value="-">-</option>
              <option value="KLAUSS">Klauss</option>
            </select>
          </div>

          <div>
            <label
              htmlFor="psico"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Psicólogo
            </label>
            <select
              id="psico"
              name="psico"
              value={newPatient.psico}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Selecione um(a) Psicólogo(a)</option>
              <option value="-">-</option>
              <option value="ANDRET">ANDRET</option>
            </select>
          </div>

          <div>
            <label
              htmlFor="nutri"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Nutricionista
            </label>
            <select
              id="nutri"
              name="nutri"
              value={newPatient.nutri}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Selecione um(a) Nutricionista</option>
              <option value="-">-</option>
              <option value="ROSANGELA">Rosangela</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Curativo
            </label>
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="curativo"
                  value="SIM"
                  checked={newPatient.curativo === true}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">Sim</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="curativo"
                  value="NAO"
                  checked={newPatient.curativo === false}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">Não</span>
              </label>
            </div>
          </div>

          <div>
            <label
              htmlFor="alimentacaoEnteral"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Alimentação Enteral
            </label>
            <select
              id="alimentacaoEnteral"
              name="alimentacaoEnteral"
              value={newPatient.alimentacaoEnteral}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Selecione uma Alimentação Enteral</option>
              <option value="SNE">SNE</option>
              <option value="GTT">GTT</option>
              <option value="SOG">SOG</option>
              <option value="X">X</option>
              <option value="vazio">Não tem</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Regime Coparticipação
            </label>
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="regimeCoparticipacao"
                  value="SIM"
                  checked={newPatient.regimeCoparticipacao === "SIM"}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">Sim</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="regimeCoparticipacao"
                  value="NAO"
                  checked={newPatient.regimeCoparticipacao === "NAO"}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">Não</span>
              </label>
            </div>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              LeMedh / Materiais
            </label>
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <select
                  onChange={handleLeMedhSelect}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={isLoadingMaterials}
                >
                  <option value="">{isLoadingMaterials ? "Carregando materiais..." : "Selecione um material"}</option>
                  {materialOptions.map((material) => (
                    <option
                      key={material.id}
                      value={material.codigo}
                      disabled={newPatient.lemedh.some(item => item.codigoMaterial === material.codigo)}
                    >
                      {material.descricao} - {material.codigo} - {formatCurrency(material.valor)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Lista de materiais adicionados */}
              {newPatient.lemedh.length > 0 && (
                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Materiais Selecionados</h3>
                  <div className="space-y-2">
                    {newPatient.lemedh.map((material, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">
                            {material.descricao || material.observacao}
                          </div>
                          <div className="text-sm text-gray-500">
                            Código: {material.codigoMaterial}
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center space-x-2">
                            <button
                              type="button"
                              onClick={() => handleUpdateMaterialQuantity(index, material.quantidade - 1)}
                              className="w-8 h-8 flex items-center justify-center bg-gray-200 rounded-lg hover:bg-gray-300"
                              disabled={material.quantidade <= 1}
                            >
                              -
                            </button>
                            <span className="w-12 text-center">{material.quantidade}</span>
                            <button
                              type="button"
                              onClick={() => handleUpdateMaterialQuantity(index, material.quantidade + 1)}
                              className="w-8 h-8 flex items-center justify-center bg-gray-200 rounded-lg hover:bg-gray-300"
                            >
                              +
                            </button>
                          </div>
                          <div className="text-right min-w-[100px]">
                            <div className="font-medium text-gray-900">
                              {formatCurrency(material.valor * material.quantidade)}
                            </div>
                            <div className="text-xs text-gray-500">
                              {formatCurrency(material.valor)} un.
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveLeMedh(index)}
                            className="text-red-600 hover:text-red-800 ml-2"
                          >
                            <BsX size={20} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Total */}
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-900">Total:</span>
                      <span className="text-lg font-bold text-blue-600">
                        {formatCurrency(calcularValorTotal())}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="md:col-span-2">
            <label
              htmlFor="observacao"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Patologia/Observações
            </label>
            <textarea
              id="observacao"
              name="observacao"
              value={newPatient.observacao}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Descreva a patologia e observações importantes..."
            />
          </div>

          <div>
            <label
              htmlFor="vacinacao"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Órgão de Vacinação
            </label>
            <select
              id="vacinacao"
              name="vacinacao"
              value={newPatient.vacinacao}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Selecione um tipo</option>
              <option value="NAO">Não tem</option>
              <option value="SAD">SAD</option>
              <option value="MINISTERIO_SAUDE">SEC DE SAÚDE</option>
            </select>
          </div>

          <div>
            <label
              htmlFor="status"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Status do Paciente
            </label>
            <select
              id="status"
              name="status"
              value={newPatient.status}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="ATIVO">Ativo</option>
              <option value="INATIVO">Inativo</option>
            </select>
          </div>
        </div>

        <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onRequestClose}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            disabled={isLoadingPatient}
          >
            {isLoadingPatient ? "Carregando..." : newPatient.id ? "Atualizar" : "Adicionar"}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default PatientModal;